import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ResendGiftCardTable from "../../components/DataTable/ResendGiftCardTable";
import PhysicalGcFulfillTable from "../../components/DataTable/PhysicalGcFulfillTable";
import PhysicalGCFulfill from "../issuance/PhysicalGCFulfill";
import instance from "../../axios";
import Pagination from "../../components/Pagination";
import { getUserToken } from "../../utils/userAuthenticate";
import BarLoading from "../../components/Loaders/BarLoading";
import { BiSearch } from "react-icons/bi";
import { MdClear } from "react-icons/md";
import { DatePicker, Input } from "antd";
import { useTranslation } from "react-i18next";
import { formatStoreCurrency } from "../../assets/currency";
import "./styles/PhysicalGcFulfillTable.css";
import "../../localisation/index"
const { RangePicker } = DatePicker;

const PhysicalGiftCardOrder = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const PER_PAGE_ITEM = 15;

  const [orders, setOrders] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // for fulfill view - sync with URL
  const [selectedOrderData, setSelectedOrderData] = useState(null);

  // for search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [filteredOrders, setFilteredOrders] = useState(null);

  const showFulfillView = !!orderId;

  // fetch orders
  const fetchOrders = async () => {
    setIsLoading(true);

    const url = `/giftcard/orders?page=${currentPage}&pageSize=${PER_PAGE_ITEM}&isPhysical=true`;
    const headers = {
      Authorization: getUserToken(),
    };

    try {
      const res = await instance.post(url, {}, { headers });
      const resData = res.data;

      if (resData.code === 200) {
        setOrders(resData);
      }
    } catch (error) {
      if (error.response) {
        console.error("server error", error.response.data);
      } else if (error.request) {
        console.error("no response received", error.request);
      } else {
        console.error("Error", error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // resend mails
  const resendMail = async (orderId) => {
    setIsLoading(true);
    const url = `/giftcard/email?order_id=${orderId}`;
    const headers = {
      Authorization: getUserToken(),
    };

    try {
      const res = await instance.post(url, {}, { headers });
      const resData = res.data;

      if (resData?.code === 200) {
        alert(resData?.message);
      } else {
        alert(resData?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // handle fulfill button click
  const handleFulfillClick = (row) => {
    const orderIdToFulfill = row?.id || row?.order_id || row?._id;
    console.log('Fulfill clicked for order:', orderIdToFulfill, 'Full row:', row);
    setSelectedOrderData(row);
    
    // Update URL with order ID in the format /physical-gift-card-orders/fulfill/:orderId
    navigate(`/physical-gift-card-orders/fulfill/${orderIdToFulfill}`);
  };

  // handle back from fulfill view
  const handleBackFromFulfill = () => {
    setSelectedOrderData(null);
    
    // Navigate back to list view
    navigate('/physical-gift-card-orders');
  };

  const handleSearch = async () => {
    setIsLoading(true);

    let url = `/giftcard/orders?page=1&pageSize=${PER_PAGE_ITEM}&isPhysical=true`;

    if (searchTerm) {
      url += `&orderNo=${searchTerm}`;
    }

    // Append date range to the URL if it exists
    if (selectedDateRange) {
      url += `&startDate=${selectedDateRange[0].format(
        "YYYY-MM-DD"
      )}&endDate=${selectedDateRange[1].format("YYYY-MM-DD")}`;
    }

    const headers = {
      Authorization: getUserToken(),
    };

    try {
      const res = await instance.post(url, {}, { headers });
      const resData = res.data;

      if (resData.code === 200) {
        setFilteredOrders(resData);
      }
    } catch (error) {
      if (error.response) {
        console.error("server error", error.response.data);
      } else if (error.request) {
        console.error("no response received", error.request);
      } else {
        console.error("Error", error.message);
      }
    } finally {
      setSearchTerm("");
      setSelectedDateRange(null);
      setIsLoading(false);
    }
  };

  // Fetch orders when not in fulfill view
  useEffect(() => {
    if (!orderId) {
      fetchOrders();
    }
  }, [currentPage, orderId]);

  // Clear selected order data when navigating away from fulfill view
  useEffect(() => {
    if (!orderId) {
      setSelectedOrderData(null);
    }
  }, [orderId]);

  // Show fulfill view if an order is selected (based on URL)
  if (showFulfillView && orderId) {
    console.log('Rendering PhysicalGCFulfill with order:', orderId);
    return (
      <PhysicalGCFulfill
        orderId={orderId}
        orderData={selectedOrderData}
        onBack={handleBackFromFulfill}
      />
    );
  }

  return (
    <div className="physical-giftcard-container component">
      <div className="component-primary-heading">{t("dashboardMenu.physicalGiftcardOrders")}</div>

      <div className="app-table__list-actions">
        <Input
          type="number"
          name="orderNumber"
          id="orderNumber"
          className="app-table__action-fields"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("resendGc.orderNumber")}
        />
        <RangePicker
          size="middle"
          className="app-table__action-fields"
          value={selectedDateRange}
          onChange={(dates) => setSelectedDateRange(dates)}
        />

        <BiSearch className="app-table__action-icons" onClick={handleSearch} />

        <MdClear
          className="app-table__action-icons"
          onClick={() => setFilteredOrders(null)}
        />
      </div>

      {isLoading === false ? (
        filteredOrders ? (
          <>
            {filteredOrders?.total >= 1 ? (
              <>
                <PhysicalGcFulfillTable
                  data={filteredOrders?.data}
                  resendMail={resendMail}
                  onFulfillClick={handleFulfillClick}
                />

                <Pagination
                  total={filteredOrders?.total}
                  perPage={PER_PAGE_ITEM}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                />
              </>
            ) : (
              <div className="section-box-container">
                <div className="section-box-title">{t("resendGc.noResults")}</div>
              </div>
            )}
          </>
        ) : (
          <>
            {orders && orders.total !== 0 ? (
              <>
                <PhysicalGcFulfillTable
                  data={orders.data}
                  resendMail={resendMail}
                  onFulfillClick={handleFulfillClick}
                />

                <Pagination
                  total={orders.total}
                  perPage={PER_PAGE_ITEM}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                />
              </>
            ) : (
              <div className="section-box-container">
                <div className="section-box-title">{t("resendGc.noGiftcards")}</div>
              </div>
            )}
          </>
        )
      ) : (
        <BarLoading />
      )}
    </div>
  );
};

export default PhysicalGiftCardOrder;